/**
 * Google Identity Services (AUTH-5): скрипт грузится один раз, отдаёт id_token
 * (credential) — его обменивает на Passport-токен `POST /auth/google`.
 */
const SCRIPT_ID = 'google-gsi-client';
const SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

let loading: Promise<GsiId> | null = null;

export function loadGoogleIdentity(): Promise<GsiId> {
  if (window.google?.accounts?.id) return Promise.resolve(window.google.accounts.id);
  if (loading) return loading;

  loading = new Promise<GsiId>((resolve, reject) => {
    const finish = () => {
      const id = window.google?.accounts?.id;
      if (id) resolve(id);
      else reject(new Error('Google Identity Services did not initialise'));
    };
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', finish, { once: true });
      existing.addEventListener(
        'error',
        () => reject(new Error('Google Identity Services failed to load')),
        { once: true },
      );
      return;
    }
    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = finish;
    s.onerror = () => {
      loading = null;
      reject(new Error('Google Identity Services failed to load'));
    };
    document.head.appendChild(s);
  });
  return loading;
}

export interface GoogleSignInHandle {
  /** Официальная кнопка рендерится в контейнер (поверх кнопки макета). */
  renderButton: (parent: HTMLElement, width: number, text: GsiButtonConfiguration['text']) => void;
  /** One Tap / явный запрос входа с клавиатуры. */
  prompt: () => void;
  cancel: () => void;
}

let initializedFor: string | null = null;
let currentCallback: ((idToken: string, selectBy: string | undefined) => void) | null = null;

/**
 * `initialize()` вызывается один раз на client id (повтор GIS считает ошибкой и
 * предупреждает в консоли — под StrictMode эффект монтируется дважды); повторные
 * вызовы только переключают получателя credential.
 */
export async function initGoogleSignIn(
  clientId: string,
  onCredential: (idToken: string, selectBy: string | undefined) => void,
): Promise<GoogleSignInHandle> {
  const id = await loadGoogleIdentity();
  currentCallback = onCredential;
  if (initializedFor !== clientId) {
    initializedFor = clientId;
    id.initialize({
      client_id: clientId,
      callback: (response) => currentCallback?.(response.credential, response.select_by),
      auto_select: false,
      cancel_on_tap_outside: true,
      context: 'signin',
      ux_mode: 'popup',
      itp_support: true,
      use_fedcm_for_prompt: true,
    });
  }
  return {
    renderButton: (parent, width, text) =>
      id.renderButton(parent, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        shape: 'rectangular',
        text,
        logo_alignment: 'center',
        width: Math.max(200, Math.min(400, Math.round(width))),
      }),
    prompt: () => id.prompt(),
    cancel: () => id.cancel(),
  };
}
